import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, LayoutGrid, FileText, Newspaper, Briefcase,
  Image as ImageIcon, MessageSquare, Users, Plus, Pencil, Trash2, ExternalLink, Download, Film,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { api, mediaUrl } from "../../lib/api";
import { CrudEditor } from "./CrudEditor";
import { MediaUploadField } from "./MediaUploadField";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutGrid },
  { id: "verticals", label: "Verticals", icon: LayoutGrid },
  { id: "blog", label: "Blog", icon: FileText },
  { id: "news", label: "News", icon: Newspaper },
  { id: "jobs", label: "Jobs", icon: Briefcase },
  { id: "media", label: "Media", icon: ImageIcon },
  { id: "messages", label: "Messages", icon: MessageSquare },
  { id: "applications", label: "Applications", icon: Users },
];

const FIELDS = {
  verticals: [
    { name: "name", label: "Name", type: "text" },
    { name: "category", label: "Category", type: "text" },
    { name: "excerpt", label: "Excerpt", type: "text" },
    { name: "image_url", label: "Image", type: "image" },
    { name: "body", label: "Body", type: "paragraphs" },
    { name: "order", label: "Order", type: "number" },
    { name: "published", label: "Published", type: "bool" },
  ],
  blog: [
    { name: "title", label: "Title", type: "text" },
    { name: "date", label: "Date (YYYY-MM-DD)", type: "text" },
    { name: "excerpt", label: "Excerpt", type: "text" },
    { name: "image_url", label: "Cover Image", type: "image" },
    { name: "body", label: "Body", type: "paragraphs" },
    { name: "published", label: "Published", type: "bool" },
  ],
  news: [
    { name: "title", label: "Title", type: "text" },
    { name: "date", label: "Date (YYYY-MM-DD)", type: "text" },
    { name: "excerpt", label: "Excerpt", type: "text" },
    { name: "image_url", label: "Cover Image", type: "image" },
    { name: "body", label: "Body", type: "paragraphs" },
    { name: "published", label: "Published", type: "bool" },
  ],
  jobs: [
    { name: "title", label: "Title", type: "text" },
    { name: "department", label: "Department", type: "text" },
    { name: "location", label: "Location", type: "text" },
    { name: "type", label: "Type", type: "select", options: ["Full-time", "Part-time", "Contract", "Internship"] },
    { name: "experience", label: "Experience", type: "text" },
    { name: "description", label: "Description", type: "paragraphs" },
    { name: "published", label: "Published", type: "bool" },
  ],
};

export default function AdminDashboard() {
  const { user, checking, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [media, setMedia] = useState([]);
  const [messages, setMessages] = useState([]);
  const [applications, setApplications] = useState([]);
  const [editor, setEditor] = useState(null); // {coll, initial}
  const [mediaKind, setMediaKind] = useState("");
  const [mediaSummary, setMediaSummary] = useState(null);

  const loadMedia = useCallback((kind) => {
    setMediaKind(kind);
    const qs = kind ? `?kind=${kind}&limit=200` : "?limit=200";
    api.get(`/admin/media${qs}`).then((r) => setMedia(r.data.items || [])).catch(() => setMedia([]));
    api.get("/admin/media/summary").then((r) => setMediaSummary(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!checking && !user) nav("/admin");
  }, [user, checking, nav]);

  const loadStats = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const loadTab = useCallback((t) => {
    if (["verticals", "blog", "news", "jobs"].includes(t)) {
      api.get(`/admin/${t}`).then((r) => setItems(r.data)).catch(() => setItems([]));
    } else if (t === "media") {
      loadMedia("");
    } else if (t === "messages") {
      api.get("/admin/messages").then((r) => setMessages(r.data)).catch(() => setMessages([]));
    } else if (t === "applications") {
      api.get("/admin/applications").then((r) => setApplications(r.data)).catch(() => setApplications([]));
    }
  }, [loadMedia]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadTab(tab); }, [tab, loadTab]);
  if (checking || !user) return null;

  const isCrud = ["verticals", "blog", "news", "jobs"].includes(tab);

  const del = async (coll, id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/admin/${coll}/${id}`);
    toast.success("Deleted");
    loadTab(coll); loadStats();
  };

  return (
    <div className="min-h-screen bg-[#030712] flex" data-testid="admin-dashboard-page">
      {/* Sidebar */}
      <aside className="w-60 border-r border-slate-800 bg-[#070b12] hidden md:flex flex-col fixed inset-y-0">
        <div className="p-6 flex items-center gap-2.5 border-b border-slate-800">
          <img src="/logo-mark.png" alt="SRAM & MRAM" className="h-8 w-auto" />
          <span className="font-display font-extrabold text-white">Admin CMS</span>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                tab === t.id ? "bg-emerald-500/15 text-emerald-400" : "text-slate-400 hover:text-white hover:bg-slate-800/50"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <button onClick={() => { logout(); nav("/admin"); }} data-testid="admin-logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:text-red-400 hover:bg-red-500/10">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 md:ml-60">
        {/* Mobile tabs */}
        <div className="md:hidden flex gap-1 overflow-x-auto p-3 border-b border-slate-800 bg-[#070b12] sticky top-0 z-20">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-mtab-${t.id}`}
              className={`px-3 py-2 rounded-lg text-xs whitespace-nowrap ${tab === t.id ? "bg-emerald-500/15 text-emerald-400" : "text-slate-400"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-6 lg:p-10">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-display font-bold text-white capitalize">{tab}</h1>
              <p className="text-slate-500 text-sm mt-1">Signed in as {user.email}</p>
            </div>
            {isCrud && (
              <button onClick={() => setEditor({ coll: tab, initial: null })} data-testid="admin-create-new-btn"
                className="btn-emerald px-5 py-2.5 rounded-full inline-flex items-center gap-2">
                <Plus className="w-4 h-4" /> New
              </button>
            )}
          </div>

          {/* OVERVIEW */}
          {tab === "overview" && stats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { k: "verticals", label: "Verticals" },
                { k: "blog", label: "Blog Posts" },
                { k: "news", label: "News" },
                { k: "jobs", label: "Jobs" },
                { k: "messages", label: "Messages", sub: `${stats.new_messages} new` },
                { k: "applications", label: "Applications", sub: `${stats.pending_applications} pending` },
              ].map((c) => (
                <div key={c.k} className="card-vertical rounded-2xl p-6" data-testid={`stat-card-${c.k}`}>
                  <p className="font-mono text-4xl font-bold text-white">{stats[c.k]}</p>
                  <p className="text-slate-400 text-sm mt-2">{c.label}</p>
                  {c.sub && <p className="text-emerald-400 text-xs mt-1 font-mono">{c.sub}</p>}
                </div>
              ))}
            </div>
          )}

          {/* CRUD LISTS */}
          {isCrud && (
            <div className="space-y-3">
              {items.length === 0 && <p className="text-slate-500 font-mono py-10">No items yet. Click “New” to create one.</p>}
              {items.map((it) => (
                <div key={it.id} className="card-vertical rounded-xl p-4 flex items-center justify-between gap-4" data-testid={`admin-row-${tab}-${it.id}`}>
                  <div className="flex items-center gap-4 min-w-0">
                    {(it.image_url) && <img src={mediaUrl(it.image_url)} alt="" className="w-14 h-14 rounded-lg object-cover border border-slate-800 flex-shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-white font-medium truncate">{it.name || it.title}</p>
                      <p className="text-slate-500 text-xs truncate">{it.category || it.department || it.date} {it.published === false && "· Draft"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => setEditor({ coll: tab, initial: it })} data-testid={`admin-edit-btn-${it.id}`}
                      className="p-2 rounded-lg text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(tab, it.id)} data-testid={`admin-delete-btn-${it.id}`}
                      className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* MEDIA */}
          {tab === "media" && (
            <div>
              <div className="max-w-md mb-6">
                <MediaUploadField value="" onChange={() => { loadMedia(mediaKind); toast.success("Added to library"); }} testid="media-upload-input" />
              </div>
              {mediaSummary && (
                <div className="flex flex-wrap gap-2 mb-6" data-testid="media-filter-bar">
                  {[
                    { k: "", label: `All (${mediaSummary.all})` },
                    { k: "image", label: `Images (${mediaSummary.image})` },
                    { k: "video", label: `Videos (${mediaSummary.video})` },
                    { k: "doc", label: `Docs / PDFs (${mediaSummary.doc})` },
                    { k: "audio", label: `Audio (${mediaSummary.audio})` },
                  ].map((f) => (
                    <button key={f.k} onClick={() => loadMedia(f.k)} data-testid={`media-filter-${f.k || "all"}`}
                      className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                        mediaKind === f.k ? "bg-emerald-500 text-slate-950 border-emerald-500" : "border-slate-800 text-slate-300 hover:border-emerald-500/50"
                      }`}>
                      {f.label}
                    </button>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {media.map((m) => (
                  <div key={m.id} className="card-vertical rounded-xl overflow-hidden group" data-testid={`media-item-${m.id}`}>
                    {m.content_type?.startsWith("image") ? (
                      <img src={mediaUrl(m.url)} alt={m.filename} loading="lazy" className="w-full h-28 object-cover" />
                    ) : m.content_type?.startsWith("video") ? (
                      <div className="w-full h-28 flex flex-col items-center justify-center bg-slate-900 text-emerald-400 gap-1"><Film className="w-6 h-6" /><span className="text-[10px] text-slate-500">VIDEO</span></div>
                    ) : (
                      <div className="w-full h-28 flex flex-col items-center justify-center bg-slate-900 text-emerald-400 gap-1"><FileText className="w-6 h-6" /><span className="text-[10px] text-slate-500 uppercase">{(m.filename || "").split(".").pop()}</span></div>
                    )}
                    <div className="p-3">
                      <p className="text-xs text-slate-400 truncate">{m.filename}</p>
                      <div className="flex gap-2 mt-2">
                        <a href={mediaUrl(m.url)} target="_blank" rel="noreferrer" className="text-emerald-400 text-xs inline-flex items-center gap-1" data-testid={`media-open-${m.id}`}><ExternalLink className="w-3 h-3" /> Open</a>
                        <button onClick={async () => { await api.delete(`/admin/media/${m.id}`); loadMedia(mediaKind); toast.success("Removed"); }} className="text-red-400 text-xs" data-testid={`media-delete-${m.id}`}>Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
                {media.length === 0 && <p className="text-slate-500 font-mono">Media library is empty.</p>}
              </div>
            </div>
          )}

          {/* MESSAGES */}
          {tab === "messages" && (
            <div className="space-y-3">
              {messages.length === 0 && <p className="text-slate-500 font-mono py-10">No contact messages yet.</p>}
              {messages.map((m) => (
                <div key={m.id} className="card-vertical rounded-xl p-5" data-testid={`message-row-${m.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-white font-medium">{m.name} <span className="text-slate-500 font-normal text-sm">· {m.email}</span></p>
                      {m.subject && <p className="text-emerald-400 text-sm mt-1">{m.subject}</p>}
                      {m.vertical && <p className="text-slate-500 text-xs mt-0.5">Interested in: {m.vertical}</p>}
                      <p className="text-slate-300 text-sm mt-2">{m.message}</p>
                      <p className="text-slate-600 text-xs mt-2 font-mono">{new Date(m.created_at).toLocaleString()}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      <select value={m.status} onChange={async (e) => { await api.patch(`/admin/messages/${m.id}?status=${e.target.value}`); loadTab("messages"); loadStats(); }}
                        data-testid={`message-status-${m.id}`}
                        className="bg-[#070b12] border border-slate-800 rounded-lg px-3 py-1.5 text-xs text-slate-200">
                        <option value="new">New</option>
                        <option value="read">Read</option>
                        <option value="replied">Replied</option>
                      </select>
                      <button onClick={async () => { await api.delete(`/admin/messages/${m.id}`); loadTab("messages"); loadStats(); }} className="text-red-400 text-xs" data-testid={`message-delete-${m.id}`}>Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* APPLICATIONS */}
          {tab === "applications" && (
            <div className="space-y-3">
              {applications.length === 0 && <p className="text-slate-500 font-mono py-10">No job applications yet.</p>}
              {applications.map((a) => (
                <div key={a.id} className="card-vertical rounded-xl p-5" data-testid={`application-row-${a.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-white font-medium">{a.name} <span className="text-slate-500 font-normal text-sm">· {a.email}</span></p>
                      <p className="text-emerald-400 text-sm mt-1">Applied for: {a.job_title}</p>
                      {a.phone && <p className="text-slate-500 text-xs mt-0.5">{a.phone}</p>}
                      {a.cover_letter && <p className="text-slate-300 text-sm mt-2">{a.cover_letter}</p>}
                      <a href={`${mediaUrl(a.resume_url)}?auth=${encodeURIComponent(document.cookie)}`} onClick={(e) => { e.preventDefault(); downloadResume(a); }}
                        className="inline-flex items-center gap-1.5 text-emerald-400 text-sm mt-3" data-testid={`application-resume-${a.id}`}>
                        <Download className="w-4 h-4" /> {a.resume_filename || "Download resume"}
                      </a>
                      <p className="text-slate-600 text-xs mt-2 font-mono">{new Date(a.created_at).toLocaleString()}</p>
                    </div>
                    <select value={a.status} onChange={async (e) => { await api.patch(`/admin/applications/${a.id}?status=${e.target.value}`); loadTab("applications"); loadStats(); }}
                      data-testid={`application-status-${a.id}`}
                      className="bg-[#070b12] border border-slate-800 rounded-lg px-3 py-1.5 text-xs text-slate-200 flex-shrink-0">
                      <option value="pending">Pending</option>
                      <option value="reviewing">Reviewing</option>
                      <option value="interviewing">Interviewing</option>
                      <option value="hired">Hired</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {editor && (
        <CrudEditor
          coll={editor.coll}
          fields={FIELDS[editor.coll]}
          initial={editor.initial}
          onClose={() => setEditor(null)}
          onSaved={() => { loadTab(editor.coll); loadStats(); }}
        />
      )}
    </div>
  );
}

// Download a protected resume through the authenticated cookie session.
async function downloadResume(a) {
  try {
    const res = await api.get(a.resume_url.replace("/api", ""), { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = a.resume_filename || "resume";
    link.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error("Could not download resume");
  }
}
